/*
 * libpannellum - A WebGL and CSS 3D transform based Panorama Renderer
 * Copyright (c) 2012-2017 Matthew Petroff
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 */

import CubemapUtil from './helpers/cubemap.util';

let pendingTextureRequests = [];

class TextureImageLoader {
    _gl = null;
    pending = true;
    image = null;

    constructor(gl, src = null, texture = null, callback = function () {
    }) {
        this._gl = gl;
        this.src = src;
        this.texture = texture;
        this.callback = callback;
    }

    static makeTexture(gl, src, texture) {
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.bindTexture(gl.TEXTURE_2D, null);

        return texture;
    }

    static makeTextureFromArrayBufferView(gl, buffer, texture, width, height, format, type) {
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, format, width, height, 0, format, type, buffer);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.bindTexture(gl.TEXTURE_2D, null);

        return texture;
    }

    loadTexture(src, texture, callback) {
        this.pending = false;
        if (texture) {
            this.texture = texture;
        }
        if (callback) {
            this.callback = callback;
        }
        if (src) {
            this.src = src;
        }
        if (!this.src || this.src.indexOf('undefined') > -1) throw new Error('No src for loading texture');

        this.image = new Image();
        this.image.crossOrigin = 'anonymous';
        this.image.addEventListener('load', () => this.onImageLoad());
        this.image.addEventListener('error', (err) => {
            this.callback(this._makeTexture(this.image, this.texture), this);
        })
        this.image.src = this.src;
    }

    onImageLoad() {
        if (this.texture instanceof Array) {
            let canvases = [];
            let width = this.image.width;
            for (let i = 0; i < this.texture.length; i++) {
                let x = 0;
                let y = i * width;

                let canvas = document.createElement('canvas'),
                    ctx = canvas.getContext('2d');
                canvas.width = width;
                canvas.height = width;
                ctx.drawImage(this.image, x, y, width, width, 0, 0, width, width);

                canvases.push(canvas);
            }
            return this.callback(canvases.map((canvas, i) => this._makeTexture(canvas, this.texture[i])), this);
        }

        this.callback(this._makeTexture(this.image, this.texture), this);
    }

    /**
     * @param {Image|HTMLCanvasElement|ArrayBuffer} src
     * @param {WebGLTexture} texture
     * @returns {WebGLTexture}
     * @private
     */
    _makeTexture(src, texture) {
        return TextureImageLoader.makeTexture(this._gl, src, texture);
    }

}

class MultiresNode {
    textureLoaded = false;
    textureLoad = false;
    diff = null;

    /**
     * Creates new multires node.
     * @constructor
     * @param {number[]} vertices - Node's verticies.
     * @param {string} side - Node's cube face.
     * @param {number} level - Node's zoom level.
     * @param {number} x - Node's x position.
     * @param {number} y - Node's y position.
     * @param {string} path - Node's path.
     */
    constructor(vertices, side, level, x, y, path) {
        this.vertices = vertices;
        this.side = side;
        this.level = level;
        this.x = x;
        this.y = y;
        this.path = path.replace('%s', side).replace('%l', level).replace('%x', x).replace('%y', y);
        // TODO: we might use method bellow to get path string,
        //      its around 4x times faster then <replace> method chain above
        //      this.path = path.substr(0, path.length - 10) + level + '_' + side + y + '_' + x;
    }
}

export default class Renderer {
    _drawInProgress = false;
    _level = -1;
    _currentNodes = [];
    _nodeCache = [];
    _nodeCacheTimestamp = 0;
    _defaultFragmentShader = {type: 'default'};
    _currentShader = this._defaultFragmentShader;
    _program = null;
    //webgl context
    _gl = null;
    //vertex shader
    _vs = null;
    //fragment shader
    _fs = null;
    _fallbackImgSize = null;
    _world = null;
    _vtmps = null;
    _pose = null;
    _image = null;
    _nadir = null;
    _cubeVertBuf = null;
    _cubeVertTexCoordBuf = null;
    _cubeVertIndBuf = null;
    _loadTexture = null;
    _webglParams = {alpha: false, depth: false, preserveDrawingBuffer: true};
    _lbuffer = null;
    _lineColor = [217.0/255, 30.0/255, 24.0/255];
    _canvas = document.createElement('canvas');
    _vertexShaderCode = ` 
        attribute vec3 a_vertCoord;
        attribute vec2 a_texCoord;

        uniform mat4 u_cubeMatrix;
        uniform mat4 u_perspMatrix;

        varying mediump vec2 v_texCoord;

        void main(void) {
            gl_Position = u_perspMatrix * u_cubeMatrix * vec4(a_vertCoord, 1.0);
    
            v_texCoord = a_texCoord;
        }`.replace(/\s*\n\s*/g, '');

    constructor(container, disableDevicePixelRatio = false, permissions) {
        this._container = container;
        this._disableDevicePixelRatio = disableDevicePixelRatio;
        this._permissions = permissions;
        this._canvas.style.width = this._canvas.style.height = '100%';
        this._canvas.classList.add('original');
        this._container.appendChild(this._canvas);
        this.destroyPannellum = false;

        this.resize();
    }


    /**
     * Initialize renderer.
     * @param {Object} image - configuration object
     * @param {string} [image.basePath] - base url to multires images
     * @param {string} image.path
     * @param {number} image.maxLevel
     * @param {string} image.extension - image extension
     * @param {number} image.tileResolution
     * @param {string} image.fallbackPath
     * @param {Object} params
     * @param {number} [params.posePitch]
     * @param {number} [params.poseRollD]
     * @param {Object} [params.nadir_url]
     * @param {{hue, contrast, brightness, saturation}} [params.filters]
     * @param {function} callback - Load callback function
     */

    // init(image, {filters, nadir_url, posePitchRadian, poseRollRadian}, callback) {
    init(image, {filters, nadir_url, posePitch, poseRoll}, callback) {
        // Default argument for image type
        this._image = image;
        this._drawInProgress = false;

        let gl = this._gl;

        // Enable WebGL on canvas
        if (!gl) {
            this._gl = gl = this._canvas.getContext('experimental-webgl', this._webglParams);
        }
        if (!gl && image.hasOwnProperty('fallbackPath') &&
            ('WebkitAppearance' in document.documentElement.style ||
                navigator.userAgent.match(/Trident.*rv[ :]*11\./) ||
                navigator.appVersion.indexOf('MSIE 10') !== -1)) {
            this._initCssFallback(nadir_url);

            return;
        }
        if (!gl) {
            console.log('Error: no WebGL support detected!');
            throw {type: 'no webgl'};
        }

        this._loadTexture = this._getLoadTexture();

        if (gl && !this._nadir && nadir_url) {
            if (gl.BLEND) {
                gl.enable(gl.BLEND);
            }
            gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
            this._loadTexture(nadir_url, texture => {
                this._nadir = texture;
            });
        }

        // If there is no WebGL, fall back to CSS 3D transform renderer.
        // While browser specific tests are usually frowned upon, the
        // fallback viewer only really works with WebKit/Blink and IE 10/11
        // (it doesn't work properly in Firefox).

        if (image.basePath) {
            image.fullpath = image.basePath + image.path;
        } else {
            image.fullpath = image.path;
        }

        let vertices = CubemapUtil.createCube();
        this._vtmps = [];
        for (let s = 0; s < 6; s++) {
            this._vtmps[s] = vertices.slice(s * 12, s * 12 + 12);
        }

        // Store horizon pitch and roll if applicable
        if (typeof posePitch === 'number' || typeof poseRoll === 'number') {
            this._pose = [
                0,
                0
            ];
        }

        // Create viewport for entire canvas
        gl.viewport(0, 0, this._canvas.width, this._canvas.height);

        let program;
        this._program = program = gl.createProgram();

        this._fs = null;
        this._vs = null;
        // Create vertex shader
        this._vs = gl.createShader(gl.VERTEX_SHADER);
        gl.shaderSource(this._vs, this._vertexShaderCode);
        gl.compileShader(this._vs);
        gl.attachShader(program, this._vs);

        if (filters) {
            this._currentShader = {type: 'with_filters', options: Object.assign({}, filters)};
        } else {
            this._currentShader = this._defaultFragmentShader;
        }
        this._applyFragmentShader();
        gl.linkProgram(program);

        // Log errors
        if (!gl.getShaderParameter(this._vs, gl.COMPILE_STATUS))
            console.log(gl.getShaderInfoLog(this._vs));
        if (!gl.getShaderParameter(this._fs, gl.COMPILE_STATUS))
            console.log(gl.getShaderInfoLog(this._fs));
        if (!gl.getProgramParameter(program, gl.LINK_STATUS))
            console.log(gl.getProgramInfoLog(program));

        // Use WebGL program
        gl.useProgram(program);

        this._initUniformLocations();

        // Look up texture coordinates location
        program.texCoordLocation = gl.getAttribLocation(program, 'a_texCoord');
        gl.enableVertexAttribArray(program.texCoordLocation);

        // Look up vertex coordinates location
        program.vertPosLocation = gl.getAttribLocation(program, 'a_vertCoord');
        gl.enableVertexAttribArray(program.vertPosLocation);

        // Create buffers
        if (!this._cubeVertBuf)
            this._cubeVertBuf = gl.createBuffer();
        if (!this._cubeVertTexCoordBuf)
            this._cubeVertTexCoordBuf = gl.createBuffer();
        if (!this._cubeVertIndBuf)
            this._cubeVertIndBuf = gl.createBuffer();
        if (!this._lbuffer)
            this._lbuffer = gl.createBuffer();

        // Bind texture coordinate buffer and pass coordinates to WebGL
        gl.bindBuffer(gl.ARRAY_BUFFER, this._cubeVertTexCoordBuf);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]), gl.STATIC_DRAW);


        // Bind square index buffer and pass indicies to WebGL
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this._cubeVertIndBuf);
        gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array([0, 1, 2, 0, 2, 3]), gl.STATIC_DRAW);

        // Check if there was an error
        if (gl.getError() !== 0) {
            console.log('Error: Something went wrong with WebGL!');
            throw {type: 'webgl error'};
        }

        this._level = -1;
        this._currentNodes = [];
        this._nodeCache = [];
        this._nodeCacheTimestamp = 0;
        this._loadTexture(encodeURI(image.basePath + 'preview.' + image.extension), tex => {
            // var sides = ['l', 'f', 'r', 'b', 'u', 'd'];
            let sides = ['f', 'b', 'u', 'd', 'l', 'r'];
            let sideAccordance = [1, 3, 4, 5, 0, 2];
            for (let s = 0; s < 6; s++) {
                let ntmp = new MultiresNode(this._vtmps[s], sides[s], 0, 0, 0, image.basePath + image.path);
                ntmp.texture = tex[sideAccordance[s]];
                ntmp.textureLoaded = true;
                ntmp.timestamp = this._nodeCacheTimestamp++;
                this._currentNodes.push(ntmp);
                this._nodeCache.push(ntmp);
            }
            callback();
        }, true);
    }



    _initUniformLocations() {
        this._program.perspUniform = this._gl.getUniformLocation(this._program, 'u_perspMatrix');
        this._program.cubeUniform = this._gl.getUniformLocation(this._program, 'u_cubeMatrix');
        this._program.colorUniform = this._gl.getUniformLocation(this._program, 'u_color');
        if (this._currentShader.type === 'with_filters') {
            this._program.nadir = this._gl.getUniformLocation(this._program, 'u_nadir');
        }
        this._gl.uniform4f(this._program.colorUniform, -1.0, 0.0, 0.0, 0.0);
    }

    /**
     * Set renderer horizon pitch and roll.
     * @memberof Renderer
     * @instance
     */
    setPose(horizonPitch, horizonRoll) {
        this._pose = [horizonPitch, horizonRoll];
    };

    /**
     * Render new view of panorama.
     * @param {number} pitch - Pitch to render at (in radians).
     * @param {number} yaw - Yaw to render at (in radians).
     * @param {number} hfov - Horizontal field of view to render with (in radians).
     * @param {Object} [params] - Extra configuration parameters.
     * @param {number} [params.roll] - Camera roll (in radians).
     * @param {boolean} [params.returnImage] - Return canvas.toDataUrl()
     */
    render(pitch, yaw, hfov, params = {}) {
        if (this.destroyPannellum) return;
        let i, s, roll,
            e3plan = params.e3plan || {},
            e3Blurplan = params.e3Blurplan || {};

        let gl = this._gl;
        // Apply pitch and roll transformation if applicable
        if (this._pose) {
            [yaw, pitch, roll] = this._applyRoll(yaw, pitch);
        }

        // If no WebGL
        if (!gl) {
            // Determine face transforms
            this._renderFallback(yaw, pitch, hfov);
            return;
        }


        let aspect = this._canvas.width / this._canvas.height;
        // Create perspective matrix
        let perspMatrix = this._makePersp(hfov, aspect, 0.1, 100.0);

        // Find correct zoom level
        this._checkZoom(hfov);

        // Create rotation matrix
        let matrix = CubemapUtil.identityMatrix3();
        matrix = CubemapUtil.rotateMatrix(matrix, -roll, 'z');
        matrix = CubemapUtil.rotateMatrix(matrix, -pitch, 'x');
        matrix = CubemapUtil.rotateMatrix(matrix, yaw, 'y');
        matrix = CubemapUtil.makeMatrix4(matrix);

        // Set matrix uniforms
        gl.uniformMatrix4fv(this._program.perspUniform, false, new Float32Array(CubemapUtil.transposeMatrix4(perspMatrix)));
        gl.uniformMatrix4fv(this._program.cubeUniform, false, new Float32Array(CubemapUtil.transposeMatrix4(matrix)));
        gl.uniform4f(this._program.colorUniform, -1.0, 0.0, 0.0, 0.0);

        // Find current nodes
        let rotPersp = CubemapUtil.rotatePersp(perspMatrix, matrix);
        this._nodeCache.sort(this._multiresNodeSort);
        if (this._nodeCache.length > 150 &&
            this._nodeCache.length > this._currentNodes.length + 40) {
            // Remove older nodes from cache
            let removed = this._nodeCache.splice(140, this._nodeCache.length - 140);
            let level0 = [];
            removed = removed.filter(function (node) {
                if (node.level === 0) {
                    level0.push(node);
                    return false;
                }
                return true;
            });
            this._nodeCache.push(...level0);
            for (i = 0; i < removed.length; i++) {
                // Explicitly delete textures
                gl.deleteTexture(removed[i].texture);
            }
        }
        this._currentNodes = [];

        let sides = ['f', 'b', 'u', 'd', 'l', 'r'];
        for (s = 0; s < 6; s++) {
            let ntmp = new MultiresNode(this._vtmps[s], sides[s], 0, 0, 0, this._image.fullpath);
            this._testMultiresNode(rotPersp, ntmp, pitch, yaw, hfov);
        }
        this._currentNodes.sort(this._multiresNodeRenderSort);

        // Unqueue any pending requests for nodes that are no longer visible
        for (i = pendingTextureRequests.length - 1; i >= 0; i--) {
            if (this._currentNodes.indexOf(pendingTextureRequests[i].node) === -1) {
                pendingTextureRequests[i].node.textureLoad = false;
                pendingTextureRequests.splice(i, 1);
            }
        }

        // Only process one tile per frame to improve responsiveness
        if (pendingTextureRequests.length === 0) {
            for (i = 0; i < this._currentNodes.length; i++) {
                let node = this._currentNodes[i];
                if (!node.texture && !node.textureLoad) {
                    node.textureLoad = true;
                    setTimeout(this._processNextTile.bind(this), 0, node);

                    // needed for not overloading event query with unneeded <setTimeouts> callbacks
                    break;
                }
            }
        }

        // Draw tiles
        this._multiresDraw();

        // Draw nadir
        if (this._nadir) {
            let angle =  Math.PI;
            if(this._permissions['nadir_orientation'] === 'viewer') angle = yaw;

            this._renderNadir(angle);
        }

        this._renderPlan(e3plan);
        this._renderBlurPlan(e3Blurplan);
        if(e3Blurplan && e3Blurplan.rectanglePoints){
            const {rectanglePoints} = e3Blurplan;

            rectanglePoints.forEach((points) => {
                this._renderBlurPlan(points);
            })
        }
        if (params.returnImage && !this.isLoading()) {
            return this._getCanvasDataUrl(params.returnImage);
        }
    }

    _getCanvasDataUrl(mimeType) {
        const gl = this._gl;
        let c = document.createElement('canvas');
        const {width, height} = this._canvas;
        let canvasCtx = c.getContext('2d');
        let imageData, buffer;
        imageData = canvasCtx.createImageData(width, height);
        buffer = new Uint8Array(imageData.data);
        gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, buffer);
        const t = new Uint32Array(buffer.buffer);
        let w = 0;
        let h = 0;
        while (t[w] && h < height) {
            w += width;
            h++;
        }
        w = 0;
        while (t[w] && w < width) {
            w++;
        }
        if (window.navigator.userAgent.match(/UCBrowser/i)) {
            let i = buffer.length - 1;
            while (i--) {
                imageData.data[i] = buffer[i];
            }
        } else {
            imageData.data.set(buffer);
        }
        c.width = w;
        c.height = h;
        canvasCtx.putImageData(imageData, 0, 0);
        canvasCtx.scale(1, -1);
        canvasCtx.drawImage(c, 0, -h);

        if (mimeType === 'canvas') {
            return c;
        }
        return c.toDataURL(mimeType);
    }

    _applyRoll(yaw, pitch, roll = 0) {
        let posePitch = this._pose[0],
            poseRoll = this._pose[1];

        // Calculate new pitch and yaw
        let orig_pitch = pitch,
            orig_yaw = yaw,
            x = Math.cos(poseRoll) * Math.sin(pitch) * Math.sin(posePitch) +
                Math.cos(pitch) * (Math.cos(posePitch) * Math.cos(yaw) +
                    Math.sin(poseRoll) * Math.sin(posePitch) * Math.sin(yaw)),
            y = -Math.sin(pitch) * Math.sin(poseRoll) +
                Math.cos(pitch) * Math.cos(poseRoll) * Math.sin(yaw),
            z = Math.cos(poseRoll) * Math.cos(posePitch) * Math.sin(pitch) +
                Math.cos(pitch) * (-Math.cos(yaw) * Math.sin(posePitch) +
                    Math.cos(posePitch) * Math.sin(poseRoll) * Math.sin(yaw));
        pitch = Math.asin(Math.max(Math.min(z, 1), -1));
        yaw = Math.atan2(y, x);

        // Calculate roll
        let v = [Math.cos(orig_pitch) * (Math.sin(poseRoll) * Math.sin(posePitch) * Math.cos(orig_yaw) -
            Math.cos(posePitch) * Math.sin(orig_yaw)),
                Math.cos(orig_pitch) * Math.cos(poseRoll) * Math.cos(orig_yaw),
                Math.cos(orig_pitch) * (Math.cos(posePitch) * Math.sin(poseRoll) * Math.cos(orig_yaw) +
                    Math.sin(orig_yaw) * Math.sin(posePitch))],
            w = [-Math.cos(pitch) * Math.sin(yaw), Math.cos(pitch) * Math.cos(yaw)];
        let roll_adj = Math.acos(Math.max(Math.min((v[0] * w[0] + v[1] * w[1]) /
            (Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]) *
                Math.sqrt(w[0] * w[0] + w[1] * w[1])), 1), -1));
        if (v[2] < 0)
            roll_adj = 2 * Math.PI - roll_adj;


        return [yaw, pitch, roll + roll_adj];
    }

    _renderPlan({points = [], hasGhostPoint = false, finished = true, border = null} = {}) {
        if (!points) return;
        //draw floor and ceiling points
        if (points.length > 1) {
            if (finished) {
                points.push(points[0]);
            }
            for (let i = 0; i < 2; i++) {
                let limit = points.length - 1;
                for (let j = 0; j < limit; j++) {
                    let p1 = points[j][i];
                    let p2 = points[j + 1][i];
                    let color = !finished && j === limit - 1 ? [255.0, 255.0, 255.0] : this._lineColor;

                    this._gl.uniform4f(this._program.colorUniform, ...color, hasGhostPoint && j >= limit - 2 ? 0.3 : 0.6);
                    this._gl.bindBuffer(this._gl.ARRAY_BUFFER, this._lbuffer);
                    this._gl.bufferData(this._gl.ARRAY_BUFFER, new Float32Array([...p1, ...p2]), this._gl.STATIC_DRAW);
                    this._gl.vertexAttribPointer(this._program.vertPosLocation, 3, this._gl.FLOAT, false, 0, 0);
                    this._gl.drawArrays(this._gl.LINES, 0, 2);

                    this._increaseLineWidth([p1, p2], color, hasGhostPoint && j >= limit - 2 ? 0.3 : 0.6, 3)
                }
            }
            if (!finished && border) {
                for (let i = 0; i < 2; i++) {
                    for (let y = 0; y < border.length; y++) {
                        let p1 = points[points.length - 1][i];
                        let p2 = border[y][i];
                        this._gl.uniform4f(this._program.colorUniform, 255.0, 255.0, 255.0, .3);
                        this._gl.bindBuffer(this._gl.ARRAY_BUFFER, this._lbuffer);
                        this._gl.bufferData(this._gl.ARRAY_BUFFER, new Float32Array([...p1, ...p2]), this._gl.STATIC_DRAW);
                        this._gl.vertexAttribPointer(this._program.vertPosLocation, 3, this._gl.FLOAT, false, 0, 0);
                        this._gl.drawArrays(this._gl.LINES, 0, 2);
                    }
                }
            }
            if (finished) {
                points.pop();
            } else {
                //close path with blue line
                for (let i = 0; i < 2; i++) {
                    let p1 = points[points.length - 1][i];
                    let p2 = points[0][i];
                    this._gl.uniform4f(this._program.colorUniform, 0.0, 0.0, 255.0, .3);
                    this._gl.bindBuffer(this._gl.ARRAY_BUFFER, this._lbuffer);
                    this._gl.bufferData(this._gl.ARRAY_BUFFER, new Float32Array([...p1, ...p2]), this._gl.STATIC_DRAW);
                    this._gl.vertexAttribPointer(this._program.vertPosLocation, 3, this._gl.FLOAT, false, 0, 0);
                    this._gl.drawArrays(this._gl.LINES, 0, 2);
                }
            }
        }
        //connect floor and ceiling points
        for (let i = 0; i < points.length; i++) {
            this._gl.uniform4f(this._program.colorUniform, 0.0, 255.0, 0.0, hasGhostPoint && i === points.length - 1 ? 0.3 : 0.6);
            this._gl.bindBuffer(this._gl.ARRAY_BUFFER, this._lbuffer);
            this._gl.bufferData(this._gl.ARRAY_BUFFER, new Float32Array([...points[i][0], ...points[i][1]]), this._gl.STATIC_DRAW);
            this._gl.vertexAttribPointer(this._program.vertPosLocation, 3, this._gl.FLOAT, false, 0, 0);
            this._gl.drawArrays(this._gl.LINES, 0, 2);
            this._increaseLineWidth(points[i], [0.0, 255.0, 0.0], hasGhostPoint && i === points.length - 1 ? 0.3 : 0.6, 3)
        }
        this._gl.uniform4f(this._program.colorUniform, -1.0, 0.0, 0.0, 0.0);
    }

    _renderBlurPlan({points = [], hasGhostPoint = false, finished = true} = {}) {
        if (!points) return;
        //draw floor and ceiling points
        if (points.length > 1) {
            if (finished) {
                points.push(points[0]);
            }
            for (let i = 0; i < 1; i++) {
                let limit = points.length - 1;
                for (let j = 0; j < limit; j++) {
                    let p1 = points[j][i];
                    let p2 = points[j + 1][i];
                    let color = !finished && j === limit - 1 ? [255.0, 255.0, 255.0] :  this._lineColor;

                    this._gl.uniform4f(this._program.colorUniform, ...color, hasGhostPoint && j >= limit - 2 ? 0.3 : 0.6);
                    this._gl.bindBuffer(this._gl.ARRAY_BUFFER, this._lbuffer);
                    this._gl.bufferData(this._gl.ARRAY_BUFFER, new Float32Array([...p1, ...p2]), this._gl.STATIC_DRAW);
                    this._gl.vertexAttribPointer(this._program.vertPosLocation, 3, this._gl.FLOAT, false, 0, 0);
                    this._gl.drawArrays(this._gl.LINES, 0, 2);

                    this._increaseLineWidth([p1, p2], color, hasGhostPoint && j >= limit - 2 ? 0.3 : 0.6, 3)
                }
            }
            if (finished) {
                points.pop();
            } else {
                //close path with blue line
                for (let i = 0; i < 1; i++) {
                    let p1 = points[points.length - 1][i];
                    let p2 = points[0][i];
                    this._gl.uniform4f(this._program.colorUniform, 0.0, 0.0, 255.0, .3);
                    this._gl.bindBuffer(this._gl.ARRAY_BUFFER, this._lbuffer);
                    this._gl.bufferData(this._gl.ARRAY_BUFFER, new Float32Array([...p1, ...p2]), this._gl.STATIC_DRAW);
                    this._gl.vertexAttribPointer(this._program.vertPosLocation, 3, this._gl.FLOAT, false, 0, 0);
                    this._gl.drawArrays(this._gl.LINES, 0, 2);
                }
            }
        }

        this._gl.uniform4f(this._program.colorUniform, -1.0, 0.0, 0.0, 0.0);
    }

    _increaseLineWidth(points, color, uniform4fValue, size) {
        for (let y = 0.0001; y < size / 1000; y += 0.005) {
            this._gl.uniform4f(this._program.colorUniform, ...color, uniform4fValue);
            this._gl.bindBuffer(this._gl.ARRAY_BUFFER, this._lbuffer);
            this._gl.bufferData(this._gl.ARRAY_BUFFER, new Float32Array([points[0][0], points[0][1], points[0][2] + y, points[1][0], points[1][1], points[1][2] + y]), this._gl.STATIC_DRAW);
            this._gl.vertexAttribPointer(this._program.vertPosLocation, 3, this._gl.FLOAT, false, 0, 0);
            this._gl.drawArrays(this._gl.LINES, 0, 2);

            this._gl.uniform4f(this._program.colorUniform, ...color, uniform4fValue);
            this._gl.bindBuffer(this._gl.ARRAY_BUFFER, this._lbuffer);
            this._gl.bufferData(this._gl.ARRAY_BUFFER, new Float32Array([points[0][0] + y, points[0][1], points[0][2] + y, points[1][0] + y, points[1][1], points[1][2] + y]), this._gl.STATIC_DRAW);
            this._gl.vertexAttribPointer(this._program.vertPosLocation, 3, this._gl.FLOAT, false, 0, 0);
            this._gl.drawArrays(this._gl.LINES, 0, 2);

            this._gl.uniform4f(this._program.colorUniform, ...color, uniform4fValue);
            this._gl.bindBuffer(this._gl.ARRAY_BUFFER, this._lbuffer);
            this._gl.bufferData(this._gl.ARRAY_BUFFER, new Float32Array([points[0][0] + y, points[0][1] + y, points[0][2], points[1][0] + y, points[1][1] + y, points[1][2]]), this._gl.STATIC_DRAW);
            this._gl.vertexAttribPointer(this._program.vertPosLocation, 3, this._gl.FLOAT, false, 0, 0);
            this._gl.drawArrays(this._gl.LINES, 0, 2);

            this._gl.uniform4f(this._program.colorUniform, ...color, uniform4fValue);
            this._gl.bindBuffer(this._gl.ARRAY_BUFFER, this._lbuffer);
            this._gl.bufferData(this._gl.ARRAY_BUFFER, new Float32Array([points[0][0], points[0][1], points[0][2] - y, points[1][0], points[1][1], points[1][2] - y]), this._gl.STATIC_DRAW);
            this._gl.vertexAttribPointer(this._program.vertPosLocation, 3, this._gl.FLOAT, false, 0, 0);
            this._gl.drawArrays(this._gl.LINES, 0, 2);

            this._gl.uniform4f(this._program.colorUniform, ...color, uniform4fValue);
            this._gl.bindBuffer(this._gl.ARRAY_BUFFER, this._lbuffer);
            this._gl.bufferData(this._gl.ARRAY_BUFFER, new Float32Array([points[0][0] - y, points[0][1], points[0][2] - y, points[1][0] - y, points[1][1], points[1][2] - y]), this._gl.STATIC_DRAW);
            this._gl.vertexAttribPointer(this._program.vertPosLocation, 3, this._gl.FLOAT, false, 0, 0);
            this._gl.drawArrays(this._gl.LINES, 0, 2);

            this._gl.uniform4f(this._program.colorUniform, ...color, uniform4fValue);
            this._gl.bindBuffer(this._gl.ARRAY_BUFFER, this._lbuffer);
            this._gl.bufferData(this._gl.ARRAY_BUFFER, new Float32Array([points[0][0] - y, points[0][1] - y, points[0][2], points[1][0] - y, points[1][1] - y, points[1][2]]), this._gl.STATIC_DRAW);
            this._gl.vertexAttribPointer(this._program.vertPosLocation, 3, this._gl.FLOAT, false, 0, 0);
            this._gl.drawArrays(this._gl.LINES, 0, 2);
        }
    }

    _renderNadir(yaw) {
        let gl = this._gl;

        const angle = yaw;

        // rotationMatrix
        // const rotationMatrix = new Float32Array([
        //     cos, 0, -sin, 0,
        //     0, 1, 0, 0,
        //     sin, 0, cos, 0,
        //     0, 0, 0, 1
        // ]);

        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
    
        const vertices = new Float32Array([
            -.37, -1, -.37,
            .37, -1, -.37,
            .37, -1, .37,
            -.37, -1, .37
        ]);

        for (let i = 0; i < vertices.length; i += 3) {
            const x = vertices[i];
            const z = vertices[i + 2];
            vertices[i] = x * cos - z * sin;
            vertices[i + 2] = x * sin + z * cos;
        }

        gl.uniform1f(this._program.nadir, 1);
        gl.bindBuffer(gl.ARRAY_BUFFER, this._cubeVertBuf);
        gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

        gl.bindTexture(gl.TEXTURE_2D, this._nadir);
        gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
        gl.uniform1f(this._program.nadir, 0);
    }

    _renderFallback(yaw, pitch, hfov) {
        let s = this._fallbackImgSize / 2;
        let devicePixelRatio = this._disableDevicePixelRatio ? 1 : (window.devicePixelRatio || 1);

        let transforms = {
            f: `translate3d(-${s + 2}px, -${s + 2}px, -${s}px)`,
            b: `translate3d(${s + 2}px, -${s + 2}px, ${s}px) rotateX(180deg) rotateZ(180deg)`,
            u: `translate3d(-${s + 2}px, -${s}px, ${s + 2}px) rotateX(270deg)`,
            d: `translate3d(-${s + 2}px, ${s}px, -${s + 2}px) rotateX(90deg)`,
            l: `translate3d(-${s}px, -${s + 2}px, ${s + 2}) + 'px) rotateX(180deg) rotateY(90deg) rotateZ(180deg)`,
            r: `translate3d(${s}px, -${s + 2}px, -${s + 2}px) rotateY(270deg)`
        };
        let focal = 1 / Math.tan(hfov / 2);
        let zoom = focal * this._canvas.width / devicePixelRatio / 2 + 'px';
        let transform = `perspective(${zoom}) translateZ(${zoom}) rotateX(${pitch}rad) rotateY(${yaw}rad)`;

        // Apply face transforms
        let faces = Object.keys(transforms);
        for (let i = 0; i < 6; i++) {
            let face = this._world.querySelector(`.pnlm-${faces[i]}face`).style;
            face.webkitTransform = transform + transforms[faces[i]];
            face.transform = transform + transforms[faces[i]];
        }
    }


    _getShader(type, options = {}) {
        switch (type) {
            case 'with_filters':
                return `
                    precision mediump float;
                    uniform sampler2D u_image;
                    varying vec2 v_texCoord;
                    uniform float u_nadir;
                    uniform mediump vec4 u_color;
                    bool isDirectional = false;
                    vec3 applyHue(vec3 aColor, float aHue)
                    {
                        float angle = radians(aHue);
                        float cosAngle = cos(angle);
                        vec3 k = vec3(0.57735, 0.57735, 0.57735);
                        return aColor * cosAngle + cross(k, aColor) * sin(angle) + k * dot(k, aColor) * (1.0 - cosAngle);
                    }
                    vec3 saturationLerp(float from, vec3 col, float t)
                    {
                        float r = (from + t * (col[0] - from));
                        float g = (from + t * (col[1] - from));
                        float b = (from + t * (col[2] - from));
                        return vec3(r, g, b);
                    }
                    vec4 applyHSBEffect(vec3 startColor)
                    {
                        float _Hue = ${options.hue.toFixed(2)} * 360.0;
                        float _Contrast = ${options.contrast.toFixed(2)} * 2.0;
                        float _Brightness = ${options.brightness.toFixed(2)} * 2.0 - 1.0;
                        float _Saturation = ${options.saturation.toFixed(2)};
                        vec4 outputColor = vec4(startColor, 1.0);
                        outputColor.rgb = applyHue(outputColor.rgb, _Hue);
                        outputColor.rgb = (outputColor.rgb - 0.5) * (_Contrast) + 0.5;
                        outputColor.rgb = outputColor.rgb + _Brightness;
                        float intensity = dot(outputColor.rgb, vec3(0.299, 0.587, 0.114) );
                        outputColor.rgb = saturationLerp(intensity, outputColor.rgb, _Saturation);
                        return outputColor;
                    }
                    void main(void) {
                        if(u_color.r > -1.0) {
                            gl_FragColor = u_color;
                        }
                        else {
                            vec4 comb_color = texture2D(u_image, v_texCoord);
                            if (comb_color.a < 0.5){
                                discard;
                            }
                            if ((v_texCoord.x <= u_nadir  && v_texCoord.y <= u_nadir)){
                                gl_FragColor = comb_color;
                            }
                            else{
                                gl_FragColor = applyHSBEffect(comb_color.rgb);
                            }
                        }
                    }`.replace(/\s*\n\s*/g, '');
                break;
        }
        return `varying mediump vec2 v_texCoord;
                uniform sampler2D u_sampler;
                uniform mediump vec4 u_color;

                void main(void) {
                    if(u_color.r > -1.0) {
                        gl_FragColor = u_color;
                    }
                    else {
                        gl_FragColor = texture2D(u_sampler, v_texCoord);
                    }
                }`.replace(/\s*\n\s*/g, '');

    }

    /**
     * Sorting method for multires nodes.
     * @private
     * @param {MultiresNode} a - First node.
     * @param {MultiresNode} b - Second node.
     * @returns {number} Base tiles first, then higher timestamp first.
     */
    _multiresNodeSort(a, b) {
        return a.level === 1 && b.level !== 1 ? -1 : b.level === 1 && a.level !== 1 ? 1 : b.timestamp - a.timestamp;
    }


    /**
     * Sorting method for multires node rendering.
     * @param {MultiresNode} a - First node.
     * @param {MultiresNode} b - Second node.
     * @returns {number} Lower zoom levels first, then closest to center first.
     */
    _multiresNodeRenderSort(a, b) {
        // Lower zoom levels first
        if (a.level !== b.level) {
            return a.level - b.level;
        }

        // Lower distance from center first
        return a.diff - b.diff;
    }

    _applyFragmentShader() {
        this._deleteFragmentShader();
        this._fs = this._gl.createShader(this._gl.FRAGMENT_SHADER);
        let code = this._getShader(this._currentShader.type, this._currentShader.options);
        this._gl.shaderSource(this._fs, code);
        this._gl.compileShader(this._fs);

        this._gl.attachShader(this._program, this._fs);
    }

    _deleteFragmentShader() {
        if (this._fs) {
            this._gl.detachShader(this._program, this._fs);
            this._gl.deleteShader(this._fs);
            this._fs = null;
        }
    }

    _initCssFallback(nadir_url) {
        // Remove old world if it exists
        if (this._world) {
            container.removeChild(this._world);
        }

        // Initialize renderer
        this._world = document.createElement('div');
        this._world = 'pnlm-world';

        // Add images
        let path;
        if (this._image.basePath) {
            path = this._image.basePath + this._image.fallbackPath;
        } else {
            path = this._image.fallbackPath;
        }
        let sides = ['f', 'r', 'b', 'l', 'u', 'd'];
        let loaded = 0;
        let onLoad = (img, side) => {
            // Draw image on canvas
            let faceCanvas = document.createElement('canvas');
            faceCanvas.className = 'pnlm-face pnlm-' + sides[side] + 'face';
            if(!this._world) return ;

            this._world.appendChild(faceCanvas);
            let faceContext = faceCanvas.getContext('2d');
            faceCanvas.width = img.width + 4;
            faceCanvas.height = img.height + 4;
            faceCanvas.style.width = faceCanvas.width + 'px';
            faceCanvas.style.height = faceCanvas.height + 'px';
            faceContext.drawImage(img, 2, 2);
            let imgData = faceContext.getImageData(0, 0, faceCanvas.width, faceCanvas.height);
            let data = imgData.data;

            // Duplicate edge pixels
            let i;
            let j;
            for (i = 2; i < faceCanvas.width - 2; i++) {
                for (j = 0; j < 4; j++) {
                    data[(i + faceCanvas.width) * 4 + j] = data[(i + faceCanvas.width * 2) * 4 + j];
                    data[(i + faceCanvas.width * (faceCanvas.height - 2)) * 4 + j] = data[(i + faceCanvas.width * (faceCanvas.height - 3)) * 4 + j];
                }
            }
            for (i = 2; i < faceCanvas.height - 2; i++) {
                for (j = 0; j < 4; j++) {
                    data[(i * faceCanvas.width + 1) * 4 + j] = data[(i * faceCanvas.width + 2) * 4 + j];
                    data[((i + 1) * faceCanvas.width - 2) * 4 + j] = data[((i + 1) * faceCanvas.width - 3) * 4 + j];
                }
            }
            for (j = 0; j < 4; j++) {
                data[(faceCanvas.width + 1) * 4 + j] = data[(faceCanvas.width * 2 + 2) * 4 + j];
                data[(faceCanvas.width * 2 - 2) * 4 + j] = data[(faceCanvas.width * 3 - 3) * 4 + j];
                data[(faceCanvas.width * (faceCanvas.height - 2) + 1) * 4 + j] = data[(faceCanvas.width * (faceCanvas.height - 3) + 2) * 4 + j];
                data[(faceCanvas.width * (faceCanvas.height - 1) - 2) * 4 + j] = data[(faceCanvas.width * (faceCanvas.height - 2) - 3) * 4 + j];
            }
            for (i = 1; i < faceCanvas.width - 1; i++) {
                for (j = 0; j < 4; j++) {
                    data[i * 4 + j] = data[(i + faceCanvas.width) * 4 + j];
                    data[(i + faceCanvas.width * (faceCanvas.height - 1)) * 4 + j] = data[(i + faceCanvas.width * (faceCanvas.height - 2)) * 4 + j];
                }
            }
            for (i = 1; i < faceCanvas.height - 1; i++) {
                for (j = 0; j < 4; j++) {
                    data[(i * faceCanvas.width) * 4 + j] = data[(i * faceCanvas.width + 1) * 4 + j];
                    data[((i + 1) * faceCanvas.width - 1) * 4 + j] = data[((i + 1) * faceCanvas.width - 2) * 4 + j];
                }
            }
            for (j = 0; j < 4; j++) {
                data[j] = data[(faceCanvas.width + 1) * 4 + j];
                data[(faceCanvas.width - 1) * 4 + j] = data[(faceCanvas.width * 2 - 2) * 4 + j];
                data[(faceCanvas.width * (faceCanvas.height - 1)) * 4 + j] = data[(faceCanvas.width * (faceCanvas.height - 2) + 1) * 4 + j];
                data[(faceCanvas.width * faceCanvas.height - 1) * 4 + j] = data[(faceCanvas.width * (faceCanvas.height - 1) - 2) * 4 + j];
            }

            // Draw image width duplicated edge pixels on canvas
            faceContext.putImageData(imgData, 0, 0);

            if (nadir_url && sides[side] === 'd') {
                let nadirImg = new Image();
                nadirImg.crossOrigin = 'anonymous';
                nadirImg.onload = function () {
                    let dWidth = faceContext.canvas.width * 0.38;
                    let dHeight = faceContext.canvas.height * 0.38;
                    let dx = faceContext.canvas.width * 0.31;
                    let dy = faceContext.canvas.height * 0.31;
                    faceContext.drawImage(nadirImg, 0, 0, nadirImg.width, nadirImg.height, dx, dy, dWidth, dHeight);
                };
                nadirImg.src = encodeURI(nadir_url);
            }

            loaded++;
            if (loaded === 6) {
                this._fallbackImgSize = img.width;
                container.appendChild(this._world);
                callback();
            }
        };
        for (let s = 0; s < 6; s++) {
            let faceImg = new Image();
            faceImg.crossOrigin = 'anonymous';
            faceImg.onload = onLoad.bind(this, faceImg, s);
            faceImg.src = encodeURI(path.replace('%s', sides[s]) + '.' + this._image.extension);
        }
    }

    /**
     * Destroy renderer.
     */
    destroy() {
        this.destroyPannellum = true;
        if (this._container) {
            if (this._canvas) {
                this._container.removeChild(this._canvas);
            }
            if (this._world) {
                this._container.removeChild(this._world);
            }
        }
        if (this._gl) {
            // The spec says this is only supposed to simulate losing the WebGL
            // context, but in practice it tends to actually free the memory.
            let extension = this._gl.getExtension('WEBGL_lose_context');
            if (extension)
                extension.loseContext();
        }
    }

    /**
     * Resize renderer (call after resizing container).
     */
    resize(handlePixelRatio = true) {
        let pixelRatio = !handlePixelRatio || this._disableDevicePixelRatio ? 1 : (window.devicePixelRatio || 1);

        this._canvas.width = this._canvas.clientWidth * pixelRatio;
        this._canvas.height = this._canvas.clientHeight * pixelRatio;
        if (this._gl) {
            this._gl.viewport(0, 0, this._canvas.width, this._canvas.height);
        }
    }

    setSize({width = '100%', height = '100%'} = {}, scale = 1, handlePixelRatio = true) {
        if(typeof width === "number" && typeof height === 'number') {
            const originalWidth = width;
            const [mw, mh] = this._gl.getParameter(this._gl.MAX_VIEWPORT_DIMS);
            const aspect = width / height;
            if(mh < height) {
                height = mh;
                width = height * aspect;
            }
            if(mw < width) {
                width = mw;
                height = width / aspect;
            }
            scale /= originalWidth / width;
            width = `${width}px`;
            height = `${height}px`;
        }
        this._canvas.style.width = width;
        this._canvas.style.height = height;
        this._canvas.style.transform = scale && scale !== 1 ? `scale(${scale})` : '';
        this.resize(handlePixelRatio);
    }

    /**
     * Check if images are loading.
     * @memberof Renderer
     * @instance
     * @returns {boolean} Whether or not images are loading.
     */
    isLoading() {
        if (this._gl) {
            for (let i = 0; i < this._currentNodes.length; i++) {
                if (!this._currentNodes[i].textureLoaded) {
                    return true;
                }
            }
        }
        return false;
    }

    getCanvasDimensions() {
        return [this._canvas.width, this._canvas.height];
    }

    isWebglSupported() {
        return !!this._gl || this._canvas.getContext('experimental-webgl', this._webglParams);
    };

    // Based on http://blog.tojicode.com/2012/03/javascript-memory-optimization-and.html

    /**
     * Draws multires nodes.
     * @private
     */
    _multiresDraw() {
        if (!this._drawInProgress) {
            this._drawInProgress = true;
            let gl = this._gl;
            for (let i = 0; i < this._currentNodes.length; i++) {
                if (this._currentNodes[i].textureLoaded) {
                    // Bind vertex buffer and pass vertices to WebGL
                    gl.bindBuffer(gl.ARRAY_BUFFER, this._cubeVertBuf);
                    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(this._currentNodes[i].vertices), gl.STATIC_DRAW);
                    gl.vertexAttribPointer(this._program.vertPosLocation, 3, gl.FLOAT, false, 0, 0);

                    // Prep for texture
                    gl.bindBuffer(gl.ARRAY_BUFFER, this._cubeVertTexCoordBuf);
                    gl.vertexAttribPointer(this._program.texCoordLocation, 2, gl.FLOAT, false, 0, 0);

                    // Bind texture and draw tile
                    gl.bindTexture(gl.TEXTURE_2D, this._currentNodes[i].texture); // Bind program.currentNodes[i].texture to TEXTURE0
                    gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
                }
            }
            this._drawInProgress = false;
        }
    }

    /**
     * Test if multires node is visible. If it is, add it to current nodes,
     * load its texture, and load appropriate child nodes.
     * @private
     * @param {number[]} rotPersp - Rotated perspective matrix.
     * @param {MultiresNode} node - Multires node to check.
     * @param {number} pitch - Pitch to check at.
     * @param {number} yaw - Yaw to check at.
     * @param {number} hfov - Horizontal field of view to check at.
     */
    _testMultiresNode(rotPersp, node, pitch, yaw, hfov) {
        if (CubemapUtil.checkSquareInView(rotPersp, node.vertices)) {
            // Calculate central angle between center of view and center of tile
            let inCurrent = false;
            let v = node.vertices;
            let x = v[0] + v[3] + v[6] + v[9];
            let y = v[1] + v[4] + v[7] + v[10];
            let z = v[2] + v[5] + v[8] + v[11];
            let r = Math.sqrt(x * x + y * y + z * z);
            let theta = Math.asin(z / r);
            let phi = Math.atan2(y, x);
            let ydiff = phi - yaw;
            ydiff += (ydiff > Math.PI) ? -2 * Math.PI : (ydiff < -Math.PI) ? 2 * Math.PI : 0;
            ydiff = Math.abs(ydiff);
            node.diff = Math.acos(Math.sin(pitch) * Math.sin(theta) + Math.cos(pitch) * Math.cos(theta) * Math.cos(ydiff));

            // Add node to current nodes and load texture if needed
            if (this._nodeCache.indexOf(node) === -1) {
                for (let k = 0; k < this._nodeCache.length; k++) {
                    if (this._nodeCache[k].path === node.path) {
                        inCurrent = true;
                        this._nodeCache[k].timestamp = this._nodeCacheTimestamp++;
                        this._nodeCache[k].diff = node.diff;
                        this._currentNodes.push(this._nodeCache[k]);
                        break;
                    }
                }
            }
            if (!inCurrent) {
                node.timestamp = this._nodeCacheTimestamp++;
                this._currentNodes.push(node);
                this._nodeCache.push(node);
            }

            // TODO: Test error
            // Create child nodes

            if (node.level < this._level) {
                let image = this._image;
                let levelOffset = node.level - image.maxLevel;
                let cubeSize = levelOffset < 0 ? (image.cubeResolution >> -levelOffset) : (image.cubeResolution << levelOffset);
                let numTiles = Math.ceil(cubeSize / image.tileResolution) - 1;
                let doubleTileSize = (cubeSize % image.tileResolution) << 1;
                let lastTileSize = (cubeSize << 1) % image.tileResolution;
                if (lastTileSize === 0) {
                    lastTileSize = image.tileResolution;
                }
                if (doubleTileSize === 0) {
                    doubleTileSize = image.tileResolution << 1;
                }
                let f = 0.5;
                if (node.x === numTiles || node.y === numTiles) {
                    f = 1.0 - image.tileResolution / (image.tileResolution + lastTileSize);
                }
                let i = 1.0 - f;
                let children = [];
                let vtmp, ntmp;
                let f1 = f, f2 = f, f3 = f, i1 = i, i2 = i, i3 = i;
                // Handle non-symmetric tiles
                if (node.x === numTiles && node.y !== numTiles) {
                    f2 = 0.5;
                    i2 = 0.5;
                    if (node.side === 'd' || node.side === 'u') {
                        f3 = 0.5;
                        i3 = 0.5;
                    }
                } else if (node.x !== numTiles && node.y === numTiles) {
                    f1 = 0.5;
                    i1 = 0.5;
                    if (node.side === 'l' || node.side === 'r') {
                        f3 = 0.5;
                        i3 = 0.5;
                    }
                }
                // Handle small tiles that have fewer than four children
                if (doubleTileSize <= image.tileResolution) {
                    if (node.x === numTiles) {
                        f1 = 0;
                        i1 = 1;
                        if (node.side === 'l' || node.side === 'r') {
                            f3 = 0;
                            i3 = 1;
                        }
                    }
                    if (node.y === numTiles) {
                        f2 = 0;
                        i2 = 1;
                        if (node.side === 'd' || node.side === 'u') {
                            f3 = 0;
                            i3 = 1;
                        }
                    }
                }

                vtmp = [v[0], v[1], v[2],
                    v[0] * f1 + v[3] * i1, v[1] * f + v[4] * i, v[2] * f3 + v[5] * i3,
                    v[0] * f1 + v[6] * i1, v[1] * f2 + v[7] * i2, v[2] * f3 + v[8] * i3,
                    v[0] * f + v[9] * i, v[1] * f2 + v[10] * i2, v[2] * f3 + v[11] * i3
                ];
                ntmp = new MultiresNode(vtmp, node.side, node.level + 1, node.x * 2, node.y * 2, image.fullpath);
                children.push(ntmp);
                if (!(node.x === numTiles && doubleTileSize <= image.tileResolution)) {
                    vtmp = [v[0] * f1 + v[3] * i1, v[1] * f + v[4] * i, v[2] * f3 + v[5] * i3,
                        v[3], v[4], v[5],
                        v[3] * f + v[6] * i, v[4] * f2 + v[7] * i2, v[5] * f3 + v[8] * i3,
                        v[0] * f1 + v[6] * i1, v[1] * f2 + v[7] * i2, v[2] * f3 + v[8] * i3
                    ];
                    ntmp = new MultiresNode(vtmp, node.side, node.level + 1, node.x * 2 + 1, node.y * 2, image.fullpath);
                    children.push(ntmp);
                }
                if (!(node.x === numTiles && doubleTileSize <= image.tileResolution) && !(node.y === numTiles && doubleTileSize <= image.tileResolution)) {
                    vtmp = [v[0] * f1 + v[6] * i1, v[1] * f2 + v[7] * i2, v[2] * f3 + v[8] * i3,
                        v[3] * f + v[6] * i, v[4] * f2 + v[7] * i2, v[5] * f3 + v[8] * i3,
                        v[6], v[7], v[8],
                        v[9] * f1 + v[6] * i1, v[10] * f + v[7] * i, v[11] * f3 + v[8] * i3
                    ];
                    ntmp = new MultiresNode(vtmp, node.side, node.level + 1, node.x * 2 + 1, node.y * 2 + 1, image.fullpath);
                    children.push(ntmp);
                }
                if (!(node.y === numTiles && doubleTileSize <= image.tileResolution)) {
                    vtmp = [v[0] * f + v[9] * i, v[1] * f2 + v[10] * i2, v[2] * f3 + v[11] * i3,
                        v[0] * f1 + v[6] * i1, v[1] * f2 + v[7] * i2, v[2] * f3 + v[8] * i3,
                        v[9] * f1 + v[6] * i1, v[10] * f + v[7] * i, v[11] * f3 + v[8] * i3,
                        v[9], v[10], v[11]
                    ];
                    ntmp = new MultiresNode(vtmp, node.side, node.level + 1, node.x * 2, node.y * 2 + 1, image.fullpath);
                    children.push(ntmp);
                }
                for (let j = 0; j < children.length; j++) {
                    this._testMultiresNode(rotPersp, children[j], pitch, yaw, hfov);
                }
            }
        }
    }

    /**
     * Creates a perspective matrix.
     * @private
     * @param {number} hfov - Desired horizontal field of view.
     * @param {number} aspect - Desired aspect ratio.
     * @param {number} znear - Near distance.
     * @param {number} zfar - Far distance.
     * @returns {number[]} Generated perspective matrix.
     */
    _makePersp(hfov, aspect, znear, zfar) {
        let fovy = 2 * Math.atan(Math.tan(hfov / 2) * this._canvas.height / this._canvas.width);
        let f = 1 / Math.tan(fovy / 2);
        return [
            f / aspect, 0, 0, 0,
            0, f, 0, 0,
            0, 0, (zfar + znear) / (znear - zfar), (2 * zfar * znear) / (znear - zfar),
            0, 0, -1, 0
        ];
    }

    _getLoadTexture() {
        let cacheTop = 4;   // Maximum number of concurrents loads
        let textureImageCache = {};

        for (let i = 0; i < cacheTop; i++) {
            textureImageCache[i] = new TextureImageLoader(this._gl);
        }

        function releaseTextureImageLoader(til) {

            if (pendingTextureRequests.length) {
                var req = pendingTextureRequests.shift();
                til.loadTexture(req.src, req.texture, req.callback);
            } else {
                textureImageCache[cacheTop++] = til;
            }

        }

        const PendingTextureRequest = function (node, src, texture, callback) {
            this.node = node;
            this.src = src;
            this.texture = texture;
            this.callback = callback;
        }

        return (src, callback, isPreview, node) => {
            let texture = isPreview
                ? (new Array(6)).fill(null).map(() => this._gl.createTexture())
                : this._gl.createTexture();

            if (cacheTop) {
                let obj = textureImageCache[--cacheTop];
                obj.loadTexture(src, texture, tex => {
                    cb(obj, tex)
                });
            } else {
                // let obj = new TextureImageLoader(this._gl, src, texture, tex => cb(obj, tex));
                // pendingTextureRequests.push(obj);
                pendingTextureRequests.push(new PendingTextureRequest(node, src, texture, (tex, obj) => {
                    cb(obj, tex)
                }));

            }

            return texture;


            function cb(obj, texture) {
                callback(texture);
                releaseTextureImageLoader(obj);
            }

        };
    }

    /**
     * Loads image and creates texture for a multires node / tile.
     * @private
     * @param {MultiresNode} node - Input node.
     */
    _processNextTile(node) {
        this._loadTexture(encodeURI(node.path + '.' + this._image.extension), function (texture) {
            node.texture = texture;
            node.textureLoaded = true;
        }, false, node);
    }

    /**
     * Finds and applies optimal multires zoom level.
     * @private
     * @param {number} hfov - Horizontal field of view to check at.
     */
    _checkZoom(hfov) {
        // return this._level = this._image.maxLevel;
        // Find optimal level
        let newLevel = 1;
        while (newLevel < this._image.maxLevel
        && this._canvas.width > (this._image.tileResolution << (newLevel - 1)) * Math.tan(hfov / 2) * 0.307) {
            newLevel++;
        }
        // Apply change
        this._level = newLevel;
    }

    adjustColor(filters) {
        if (filters) {
            this._currentShader = {type: 'with_filters', options: Object.assign({}, filters)};
        } else {
            this._currentShader = this._defaultFragmentShader;
        }
        this._applyFragmentShader();

        if (!this._gl.getShaderParameter(this._fs, this._gl.COMPILE_STATUS))
            console.log(this._gl.getShaderInfoLog(this._fs));
        this._gl.linkProgram(this._program);
        this._initUniformLocations();
    }
}
